/**
 * Invoicing Incident Detail Page
 *
 * Dedicated page (not a dialog) to work a single electronic-invoicing
 * incident: inspect the JSON sent/received, resend / sync against Hacienda,
 * and resolve it with a note. Data is fetched in real time (no cache) so the
 * status always reflects the latest state.
 */

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  Send,
} from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useToast } from '@/components/ui/toaster'
import { useAuth } from '@/hooks/useAuth'
import {
  IncidentWorkflowStatus,
  useIncident,
  useUpdateIncident,
  useSubmitDocument,
  useSyncDocumentStatus,
} from '@/hooks/useInvoicing'
import { t } from '@/i18n'
import { cn, parseApiError } from '@/lib/utils'
import {
  FriendlyErrors,
  HaciendaStatusBadge,
  JsonBlock,
  KIND_LABELS,
  TYPE_LABELS,
  WORKFLOW_CONFIG,
  formatCurrency,
  formatDate,
  formatJson,
  haciendaMessageTone,
} from './incidentShared'

export function InvoicingIncidentDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const incidentId = id ? Number(id) : null
  const { isAdmin } = useAuth()
  const { toast } = useToast()

  const { data: incident, isLoading, refetch, isFetching } = useIncident(incidentId)
  const updateIncident = useUpdateIncident()
  const submitDocument = useSubmitDocument()
  const syncStatus = useSyncDocumentStatus()

  const [note, setNote] = useState('')
  const [expandedAttempt, setExpandedAttempt] = useState<number | null>(null)

  // Sync the note field whenever a different incident loads.
  const loadedNote = incident?.resolution_notes ?? ''
  const [lastLoadedId, setLastLoadedId] = useState<number | null>(null)
  if (incident && incident.id !== lastLoadedId) {
    setLastLoadedId(incident.id)
    setNote(loadedNote)
    setExpandedAttempt(null)
  }

  const goBack = () => navigate('/settings/invoicing-incidents')

  if (!isAdmin) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">{t('incidents.noPermission')}</p>
      </div>
    )
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast({ title: t('incidents.detail.copied', { label }) })
  }

  const setStatus = (status: IncidentWorkflowStatus, requireNote: boolean) => {
    if (requireNote && !note.trim()) {
      toast({
        title: t('incidents.detail.noteMissing'),
        description: t('incidents.detail.noteMissingHint'),
        variant: 'destructive',
      })
      return
    }
    updateIncident.mutate(
      { id: incident!.id, payload: { status, resolution_notes: note } },
      {
        onSuccess: () => toast({ title: t('incidents.detail.updated') }),
        onError: () => toast({ title: t('incidents.detail.updateError'), variant: 'destructive' }),
      }
    )
  }

  const handleResend = () => {
    if (!incident?.bill_id) return
    submitDocument.mutate(
      { billId: incident.bill_id },
      {
        onSuccess: (res: { success?: boolean; message?: string }) => {
          toast({
            title: res?.success ? t('incidents.detail.resent') : t('incidents.detail.resentWithError'),
            description: res?.message,
            variant: res?.success ? undefined : 'destructive',
          })
        },
        onError: () => toast({ title: t('incidents.detail.resendError'), variant: 'destructive' }),
      }
    )
  }

  const handleSync = () => {
    if (!incident?.bill_id) return
    syncStatus.mutate(incident.bill_id, {
      onSuccess: (res: { success?: boolean; message?: string }) =>
        toast({
          title: res?.success === false ? t('incidents.detail.syncFailed') : t('incidents.detail.synced'),
          description: res?.message,
          variant: res?.success === false ? 'destructive' : undefined,
        }),
      onError: (err) =>
        toast({
          title: t('incidents.detail.syncError'),
          description: parseApiError(err),
          variant: 'destructive',
        }),
    })
  }

  const isBill = incident?.document_kind === 'bill'
  const isTerminal = incident?.status === 'resolved' || incident?.status === 'dismissed'

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={goBack} aria-label={t('common.back')}>
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold tracking-tight truncate">
            {t('incidents.detail.title', { id: incident?.id ?? incidentId ?? '' })}
          </h1>
          <p className="text-sm text-muted-foreground truncate">
            {incident
              ? `${TYPE_LABELS[incident.incident_type] || incident.incident_type} · ${KIND_LABELS[incident.document_kind] || incident.document_kind}`
              : t('common.loading')}
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching} aria-label={t('common.refresh')}>
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} aria-hidden="true" />
        </Button>
      </div>

      {isLoading || !incident ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Summary */}
          <Card>
            <CardContent className="p-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">{t('incidents.detail.client')}</p>
                <p className="font-medium">{incident.client_name || t('incidents.generalClient')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('incidents.detail.amount')}</p>
                <p className="font-medium">{formatCurrency(incident.total)}</p>
              </div>
              {incident.consecutive && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">{t('incidents.detail.consecutive')}</p>
                  <p className="font-mono text-xs">{incident.consecutive}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('incidents.detail.haciendaStatus')}</p>
                <HaciendaStatusBadge status={incident.current_hacienda_status} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('incidents.detail.incidentStatus')}</p>
                <Badge
                  variant="secondary"
                  className={cn('text-xs', WORKFLOW_CONFIG[incident.status].color)}
                >
                  {WORKFLOW_CONFIG[incident.status].label}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Hacienda message — tone follows the live status. When the document
              succeeded (or is just in-flight) we keep the existing summary box.
              When it's a real error we render every parsed error with a friendly
              title + suggested action, with the raw text accessible below. */}
          {(() => {
            const message = incident.current_hacienda_message || incident.error_message
            if (!message && incident.errors.length === 0) return null
            const tone = haciendaMessageTone(
              incident.current_hacienda_status,
              incident.incident_type
            )
            if (tone === 'error') {
              return (
                <FriendlyErrors
                  errors={incident.errors}
                  rawMessage={message}
                />
              )
            }
            // `tone` is narrowed to 'ok' | 'info' after the early-return above.
            const toneClass = {
              ok: 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300',
              info: 'bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300',
            }[tone]
            const title = tone === 'ok' ? t('incidents.detail.acceptedByHacienda') : t('incidents.detail.statusInHacienda')
            return (
              <div className={cn('p-3 rounded-lg text-xs', toneClass)}>
                <p className="font-medium mb-1">{title}</p>
                <p className="whitespace-pre-wrap">{message}</p>
              </div>
            )
          })()}

          {/* Bill actions: resend / sync */}
          {isBill && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleResend} disabled={submitDocument.isPending}>
                {submitDocument.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {t('incidents.detail.resend')}
              </Button>
              {incident.consecutive && (
                <Button variant="outline" onClick={handleSync} disabled={syncStatus.isPending}>
                  {syncStatus.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {t('incidents.detail.sync')}
                </Button>
              )}
            </div>
          )}

          {/* JSON sent / received per attempt */}
          {incident.attempts.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t('incidents.detail.attempts', { count: incident.attempts.length })}</p>
              {incident.attempts.map((a) => (
                <Collapsible
                  key={a.id}
                  open={expandedAttempt === a.id}
                  onOpenChange={(o) => setExpandedAttempt(o ? a.id : null)}
                >
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between h-auto py-2 px-3 border rounded-lg">
                      <span className="text-sm">
                        {t('incidents.detail.attempt', { n: a.attempt_number })} · {a.status}
                        {a.error_code ? ` · ${a.error_code}` : ''}
                      </span>
                      {expandedAttempt === a.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 mt-2">
                    {a.request_payload && (
                      <JsonBlock
                        label={t('incidents.detail.jsonSent')}
                        json={formatJson(a.request_payload)}
                        onCopy={() => copyToClipboard(formatJson(a.request_payload!), t('incidents.detail.jsonSent'))}
                      />
                    )}
                    {a.response_payload && (
                      <JsonBlock
                        label={t('incidents.detail.jsonReceived')}
                        json={formatJson(a.response_payload)}
                        onCopy={() => copyToClipboard(formatJson(a.response_payload!), t('incidents.detail.jsonReceived'))}
                      />
                    )}
                    {!a.request_payload && !a.response_payload && (
                      <p className="text-xs text-muted-foreground px-1">
                        {t('incidents.detail.noPayload')}
                      </p>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}

          {/* Resolution note */}
          <div>
            <p className="text-sm font-medium mb-1">{t('incidents.detail.resolutionNote')}</p>
            <p className="text-xs text-muted-foreground mb-2">
              {t('incidents.detail.resolutionNoteHint')}
            </p>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('incidents.detail.resolutionNotePlaceholder')}
              aria-label={t('incidents.detail.resolutionNote')}
              rows={3}
            />
            {incident.resolved_by_name && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('incidents.detail.closedBy', { name: incident.resolved_by_name, date: formatDate(incident.resolved_at) })}
              </p>
            )}
          </div>

          {/* Workflow actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {!isTerminal && incident.status !== 'in_progress' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStatus('in_progress', false)}
                disabled={updateIncident.isPending}
              >
                {t('incidents.detail.markInProgress')}
              </Button>
            )}
            {!isTerminal && incident.status === 'in_progress' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStatus('open', false)}
                disabled={updateIncident.isPending}
              >
                {t('incidents.detail.backToOpen')}
              </Button>
            )}
            {!isTerminal && (
              <>
                <Button
                  size="sm"
                  onClick={() => setStatus('resolved', true)}
                  disabled={updateIncident.isPending}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {t('incidents.detail.markResolved')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStatus('dismissed', true)}
                  disabled={updateIncident.isPending}
                >
                  <Ban className="h-4 w-4 mr-2" />
                  {t('incidents.detail.dismiss')}
                </Button>
              </>
            )}
            {isTerminal && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStatus('open', false)}
                disabled={updateIncident.isPending}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('incidents.detail.reopen')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
